import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react'
import { useAuthStore } from '@/store/authStore'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useProfileStore } from '@/store/profileStore'
import type { UserProfile } from '@/types/auth'

interface PermissionContextType {
  // State
  permissions: string[]
  activeProfile: UserProfile | null
  profiles: UserProfile[]
  
  // Loading state for permissions
  isLoadingPermissions: boolean

  // Permission checks
  hasPermission: (code: string) => boolean
  hasAnyPermission: (codes: string[]) => boolean
  hasAllPermissions: (codes: string[]) => boolean

  // Role checks
  isOrganization: boolean
  isOperation: boolean
  isProvider: boolean
  isClient: boolean
  isBusinessClient: boolean
  isSuperuser: boolean

  // Profile management
  activeProfileId: string | null
  setActiveProfile: (profileId: string) => void
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined)

interface PermissionProviderProps {
  children: ReactNode
}

const ENTITY_PROFILE_TYPES = ['ENTITY', 'ORGANIZATION']

/**
 * Permission codes granted to every Business Client session.
 * These mirror what a typical client profile receives from the backend
 * and ensure the create-order flow (search client, add client,
 * add beneficiary, payment, etc.) works without backend changes.
 */
const BUSINESS_CLIENT_PERMISSION_CODES: string[] = [
  'orders.create',
  'orders.view_own',
  'orders.update_own',
  'orders.cancel_own',
  'beneficiaries.manage',
  'profile.view_own',
  'profile.update_own',
]

export function PermissionProvider({ children }: PermissionProviderProps) {
  const { user: storeUser, isAuthenticated } = useAuthStore()
  const { user: fetchedUser, isLoading: isLoadingUser } = useCurrentUser()
  const profileMode = useProfileStore((s) => s.activeMode)
  
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null)
  const sessionUserId = fetchedUser?.id ?? storeUser?.id
  const prevSessionUserIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!sessionUserId) {
      setActiveProfileId(null)
      prevSessionUserIdRef.current = undefined
      return
    }
    if (
      prevSessionUserIdRef.current !== undefined &&
      prevSessionUserIdRef.current !== sessionUserId
    ) {
      setActiveProfileId(null)
    }
    prevSessionUserIdRef.current = sessionUserId
  }, [sessionUserId])

  const user = fetchedUser || storeUser

  const profiles = useMemo(() => {
    return user?.profiles || []
  }, [user?.profiles])

  // When the profile mode changes, switch the active profile to match
  useEffect(() => {
    if (!profiles.length) return
    if (profileMode === 'business_client') {
      const bc = profiles.find((p) => p.profile_type === 'BUSINESS_CLIENT')
      if (bc) setActiveProfileId(bc.profile_id)
    } else {
      const entity = profiles.find((p) => ENTITY_PROFILE_TYPES.includes(p.profile_type))
      if (entity) {
        setActiveProfileId(entity.profile_id)
      } else {
        const primary = profiles.find((p) => p.is_primary) || profiles[0]
        if (primary) setActiveProfileId(primary.profile_id)
      }
    }
  }, [profileMode, profiles])

  const activeProfile = useMemo(() => {
    if (!profiles.length) return null
    
    if (activeProfileId) {
      const found = profiles.find((p) => p.profile_id === activeProfileId)
      if (found) return found
    }
    
    return profiles.find((p) => p.is_primary) || profiles[0]
  }, [profiles, activeProfileId])
  
  useEffect(() => {
    if (profiles.length > 0 && !activeProfileId) {
      const primary = profiles.find((p) => p.is_primary) || profiles[0]
      if (primary) {
        setActiveProfileId(primary.profile_id)
      }
    }
  }, [profiles, activeProfileId])

  // Get permissions from active profile.
  // In business client mode, augment with guaranteed codes so the
  // create-order flow works even if the backend profile is sparse.
  const permissions = useMemo(() => {
    const backendCodes = activeProfile?.permission_codes || []
    if (profileMode === 'business_client' || activeProfile?.profile_type === 'BUSINESS_CLIENT') {
      const merged = new Set([...backendCodes, ...BUSINESS_CLIENT_PERMISSION_CODES])
      return Array.from(merged)
    }
    return backendCodes
  }, [activeProfile, profileMode])
  
  // Check if user is superuser (has full access)
  const isSuperuser = useMemo(() => {
    return user?.is_superuser || user?.admin_permissions?.is_superuser || false
  }, [user])

  // Permission check functions
  const hasPermission = (code: string): boolean => {
    // Superusers have all permissions
    if (isSuperuser) return true
    // Check for admin.full_access permission
    if (permissions.includes('admin.full_access')) return true
    return permissions.includes(code)
  }

  const hasAnyPermission = (codes: string[]): boolean => {
    if (isSuperuser) return true
    if (permissions.includes('admin.full_access')) return true
    return codes.some((code) => permissions.includes(code))
  }

  const hasAllPermissions = (codes: string[]): boolean => {
    if (isSuperuser) return true
    if (permissions.includes('admin.full_access')) return true
    return codes.every((code) => permissions.includes(code))
  }

  // Role checks based on profile type
  const isOrganization = activeProfile?.profile_type === 'ORGANIZATION'
  const isOperation = activeProfile?.profile_type === 'OPERATION'
  const isProvider = activeProfile?.profile_type === 'PROVIDER'
  const isClient = activeProfile?.profile_type === 'CLIENT'
  const isBusinessClient = activeProfile?.profile_type === 'BUSINESS_CLIENT'

  // Profile switching (for users with multiple profiles)
  const setActiveProfile = (profileId: string) => {
    const profile = profiles.find((p) => p.profile_id === profileId)
    if (profile) {
      setActiveProfileId(profileId)
      console.log('Switched to profile:', profile.display_name, profile.profile_type)
    } else {
      console.warn('Profile not found:', profileId)
    }
  }

  const value: PermissionContextType = {
    permissions,
    activeProfile,
    profiles,
    isLoadingPermissions: isAuthenticated && isLoadingUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isOrganization,
    isOperation,
    isProvider,
    isClient,
    isBusinessClient,
    isSuperuser,
    activeProfileId,
    setActiveProfile,
  }

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  )
}

export function usePermissionContext() {
  const context = useContext(PermissionContext)
  if (context === undefined) {
    throw new Error('usePermissionContext must be used within a PermissionProvider')
  }
  return context
}
